import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

export const useUnreadAnnouncements = () => {
    const { user } = useAuth();
    const [unreadCount, setUnreadCount] = useState(0);
    const [hasUnread, setHasUnread] = useState(false);
    const [unreadIds, setUnreadIds] = useState([]);
    const [announcements, setAnnouncements] = useState([]);

    useEffect(() => {
        if (!user) {
            setUnreadCount(0);
            setHasUnread(false);
            setUnreadIds([]);
            setAnnouncements([]);
            return;
        }

        const userId = user.discordId || user.id;

        const checkUnread = async () => {
            try {
                // 1. Fetch all visible announcements
                const { data: allAnnouncements, error: annError } = await supabase
                    .from('announcements')
                    .select('*')
                    .eq('visible', true)
                    .order('created_at', { ascending: false });

                if (annError) {
                    if (annError.code === 'PGRST204' || annError.code === 'PGRST205' || annError.code === '42P01' || annError.message?.includes('not found')) {
                        logger.debug('Announcements table not found or unavailable, skipping fetch (Graceful fallback)');
                        return;
                    }
                    logger.error('Error fetching announcements:', annError);
                    return;
                }

                // 2. Fetch read receipts for this user
                const { data: reads, error: readsError } = await supabase
                    .from('announcement_reads')
                    .select('announcement_id, read_at')
                    .eq('user_id', String(userId));

                if (readsError) {
                    if (readsError.code === 'PGRST116' || readsError.code === 'PGRST204' || readsError.code === 'PGRST205' || readsError.code === '42P01' || readsError.message?.includes('not found')) {
                        logger.debug('Announcement reads table not found or unavailable, ignoring receipts.');
                    } else {
                        logger.error('Error fetching announcement reads:', readsError);
                    }
                }

                // Map announcement_id -> read_at
                const readsMap = new Map((reads || []).map(r => [r.announcement_id, r.read_at]));

                // 3. Filter announcements relevant to the user
                const visibleAnnouncements = allAnnouncements.filter(announcement => {
                    // Filter: Targeted announcements (if set, must match user)
                    if (announcement.target_user_id &&
                        String(announcement.target_user_id) !== String(userId)) {
                        return false;
                    }

                    // Filter: Internal type linked to staff access
                    if (announcement.type === 'internal' && user.accessLevel === 'user') {
                        return false;
                    }

                    return true;
                });

                // 4. Calculate unread status
                const unread = visibleAnnouncements.filter(announcement => {
                    // Check if ID is in readsMap
                    return !readsMap.has(announcement.id);
                });

                // Add "read" property to the announcement objects for UI convenience
                const announcementsWithStatus = visibleAnnouncements.map(a => ({
                    ...a,
                    isRead: readsMap.has(a.id),
                    readAt: readsMap.get(a.id)
                }));

                setAnnouncements(announcementsWithStatus);
                setUnreadCount(unread.length);
                setHasUnread(unread.length > 0);
                setUnreadIds(unread.map(a => a.id));

            } catch (err) {
                logger.error('Unexpected error checking announcements:', err);
            }
        };

        checkUnread();

        // Realtime Subscription
        // Listen to both tables?
        const channel = supabase
            .channel('announcements-all')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'announcements' }, checkUnread)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'announcement_reads', filter: `user_id=eq.${userId}` }, checkUnread)
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user]);

    return { unreadCount, hasUnread, unreadIds, announcements };
};

